import React from 'react';
import { PuffLoader } from 'react-spinners';
import { Button, Input, InputTextArea, Label, RadioSet, SelectBox } from 'components';
import {
  registerPolarizationList,
  registerModulationList,
  reedSolomonCodeList,
  registerPCMCodingList,
  registerConvolutionalCodeList
} from './utils';
import { FormData, formatNumber } from 'utils/validation';
import { useTranslation } from 'react-i18next';

interface Props {
  formData: FormData;
  onNext: (data: FormData) => void;
  errors: Partial<Record<keyof FormData, string>>;
  handleInputChange: (field: keyof FormData, value: any) => void;
  isLoading: boolean;
  error: string | null;
  handleFetchTLE: () => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const SDownLink: React.FC<Props> = ({
  formData,
  errors,
  handleInputChange,
  isLoading,
  error,
  handleFetchTLE,
  handleKeyDown
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <Input
        label={t('manage.modal.name')}
        labelTooltip={t('manage.tooltip.name')}
        value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)}
        errorMessage={errors.name}
      />
      {/* NORAD + 버튼 + 로더 : 상단 정렬로 고정 */}
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Input
            label={t('manage.modal.noradId')}
            labelTooltip={t('manage.tooltip.noradId')}
            value={formData.noradId}
            onChange={(e) => handleInputChange('noradId', e.target.value)}
            onKeyDown={handleKeyDown}
            errorMessage={errors.noradId}
            placeholder={t('manage.placeholder.noradId')}
          />
        </div>

        {/* 버튼: 항상 상단 기준으로 고정 */}
        <Button
          className="self-start h-8 mt-6"
          onClick={handleFetchTLE}
          type="secondary"
          size="small"
        >
          {t('manage.button.getTle')}
        </Button>

        {/* 로더: 버튼과 같은 기준선 */}
        {isLoading && (
          <div className="self-start mt-6">
            <PuffLoader size={30} />
          </div>
        )}
      </div>
      {error && <div className="text-red-500">{error}</div>}
      <Input
        label={t('manage.modal.tleTitle')}
        value={formData.tleTitle}
        onChange={(e) => handleInputChange('tleTitle', e.target.value)}
        errorMessage={errors.tleTitle}
      />
      <Input
        label={t('manage.modal.tleLine1')}
        value={formData.tleFirst}
        onChange={(e) => handleInputChange('tleFirst', e.target.value)}
        errorMessage={errors.tleFirst}
      />
      <Input
        label={t('manage.modal.tleLine2')}
        value={formData.tleSecond}
        onChange={(e) => handleInputChange('tleSecond', e.target.value)}
        errorMessage={errors.tleSecond}
      />
      <Input
        label={t('manage.modal.centerFrequency')}
        labelTooltip={t('manage.tooltip.centerFrequency')}
        value={formData.centerFrequency}
        placeholder={t('manage.placeholder.centerFrequency')}
        onChange={(e) => handleInputChange('centerFrequency', formatNumber(e.target.value))}
        errorMessage={errors.centerFrequency}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.modulation')}
        list={registerModulationList}
        value={formData.modulation}
        onChange={(value) => handleInputChange('modulation', value)}
      />
      <Input
        label={t('manage.modal.bitRate')}
        labelTooltip={t('manage.tooltip.bitRate')}
        placeholder={t('manage.placeholder.bitRate')}
        value={formData.bitRate}
        onChange={(e) => handleInputChange('bitRate', formatNumber(e.target.value))}
        errorMessage={errors.bitRate}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.polarization')}
        list={registerPolarizationList}
        value={formData.polarization}
        onChange={(value) => handleInputChange('polarization', value)}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.convolutionalCode')}
        list={registerConvolutionalCodeList}
        value={formData.convolutionalCode}
        onChange={(value) => handleInputChange('convolutionalCode', value)}
      />
      <RadioSet
        label={t('manage.modal.reedsolomonCode')}
        list={reedSolomonCodeList}
        value={formData.reedSolomonCode}
        onChange={(value) => handleInputChange('reedSolomonCode', value)}
      />
      <Input
        label={t('manage.modal.subFrequency')}
        labelTooltip={t('manage.tooltip.subFrequency')}
        placeholder={t('manage.placeholder.subFrequency')}
        value={formData.subcarrierFrequency}
        onChange={(e) => handleInputChange('subcarrierFrequency', formatNumber(e.target.value))}
        errorMessage={errors.subcarrierFrequency}
      />
      <SelectBox
        className="max-w-none"
        bgColor="layer3"
        label={t('manage.modal.pcmCoding')}
        list={registerPCMCodingList}
        value={formData.pcmCoding}
        onChange={(value) => handleInputChange('pcmCoding', value)}
      />
      <div className="flex flex-col w-full">
        <div className="flex flex-row">
          <Label
            label={t('manage.modal.frameSyncWord')}
            htmlFor="framesyncword"
            labelTooltip={t('manage.tooltip.frameSyncWord')}
          />
        </div>
        <div className="flex flex-col w-full">
          <div className="flex flex-row justify-between items-center gap-1">
            <label htmlFor="framesyncword" className="text-text-secondary font-regular text-body2">0x</label>
            <input
              type="text"
              id="framesyncword"
              className="h-8 flex-1 rounded border bg-transparent px-3 text-body"
              placeholder="1ACFFC1D (32bit, 4byte)"
              value={formData.framesyncword}
              onChange={(e) => handleInputChange('framesyncword', e.target.value)}
            />
          </div>
          {errors.framesyncword && (
            <p className="mt-2 text-red-500 text-body2">{errors.framesyncword}</p>
          )}
        </div>
      </div>
      <Input
        label={t('manage.modal.frameSize')}
        value={formData.framesize}
        onChange={(e) => handleInputChange('framesize', formatNumber(e.target.value))}
        errorMessage={errors.framesize}
      />
      <div className="flex flex-col gap-2">
        <Label
          label={t('manage.modal.syncStrategy')}
          htmlFor={'Synchronization Strategy'}
          labelTooltip={t('manage.tooltip.syncStrategy')}
        />
        <div className="grid grid-cols-4 gap-2">
          <Input
            label={t('manage.modal.syn')}
            type="number"
            value={formData.syn.toString()}
            onChange={(e) => handleInputChange('syn', Number(e.target.value))}
            errorMessage={errors.syn}
          />
          <Input
            label={t('manage.modal.ctl')}
            type="number"
            value={formData.ctl.toString()}
            onChange={(e) => handleInputChange('ctl', Number(e.target.value))}
            errorMessage={errors.ctl}
          />
          <Input
            label={t('manage.modal.lts')}
            type="number"
            value={formData.lts.toString()}
            onChange={(e) => handleInputChange('lts', Number(e.target.value))}
            errorMessage={errors.lts}
          />
          <Input
            label={t('manage.modal.slip')}
            type="number"
            value={formData.slip.toString()}
            onChange={(e) => handleInputChange('slip', Number(e.target.value))}
            errorMessage={errors.slip}
          />
        </div>
      </div>
      <InputTextArea
        label={t('manage.modal.other')}
        value={formData.other}
        onChange={(value) => handleInputChange('other', value)}
      />
    </div>
  );
};

export default SDownLink;